import React, { useCallback, useEffect, useRef, useState } from 'react'
import { HolidayPortal } from '../holiday-portal'

// iPadOS Safari paints an animated GIF as a static bitmap once that image is
// promoted to its own layer. will-change: transform, a CSS filter, and rewriting
// transform every frame all do that, so the spider keeps sliding while the walk
// cycle never advances. The same thing happens when Auto-Play Animated Images
// is off. Step a sprite sheet from this loop instead; those style updates are
// ordinary paints and still run on the iPads where the GIF stays frozen.
const SPIDER_SPRITE_URL = '/halloween/spider-walking-sprite.png'
const SPIDER_FRAME_COUNT = 11
const SPIDER_FRAME_DURATION_MS = 100
const SPIDER_FRAME_WIDTH = 135
const SPIDER_FRAME_HEIGHT = 105

interface Spider {
  id: number
  x: number
  y: number
  angle: number
  speed: number
  targetAngle: number
  size: number
  opacity: number
  lastAngleChange: number
  frameIndex: number
  frameElapsed: number
  element: HTMLDivElement | null
  sprite: HTMLDivElement | null
}

const SpiderWalkingEffect: React.FC = () => {
  const animationRef = useRef<number>(null)
  const lastUpdateRef = useRef<number>(0)
  const spidersRef = useRef<Spider[]>([])
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerMounted, setContainerMounted] = useState(false)

  const setContainerRef = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node
    setContainerMounted(node !== null)
  }, [])

  useEffect(() => {
    if (!containerMounted) {
      return
    }

    const container = containerRef.current
    if (!container) {
      return
    }

    // Create 1-3 spiders randomly
    const spiderCount = Math.floor(Math.random() * 3) + 1 // 1, 2, or 3 spiders
    const currentTime = Date.now()
    const spiders: Spider[] = []

    // Create spider elements
    for (let i = 0; i < spiderCount; i++) {
      const angle = Math.random() * 360
      const size = Math.round(Math.random() * 40 + 30)
      const frameHeight = (size * SPIDER_FRAME_HEIGHT) / SPIDER_FRAME_WIDTH
      const spider: Spider = {
        id: i,
        x: Math.random() * (window.innerWidth - 100),
        y: Math.random() * (window.innerHeight - 100),
        angle,
        speed: Math.random() * 0.3 + 0.8,
        targetAngle: angle,
        size,
        opacity: 1,
        lastAngleChange: currentTime,
        frameIndex: Math.floor(Math.random() * SPIDER_FRAME_COUNT),
        frameElapsed: Math.random() * SPIDER_FRAME_DURATION_MS,
        element: null,
        sprite: null,
      }

      // Rotation stays on the wrapper. The walk cycle is a separate element so
      // iPadOS cannot treat the sprite as a transform-only composited image.
      const spiderElement = document.createElement('div')
      spiderElement.setAttribute('aria-hidden', 'true')
      spiderElement.style.position = 'absolute'
      spiderElement.style.transform = `translate(-50%, -50%) rotate(${spider.angle}deg)`
      spiderElement.style.left = `${spider.x}px`
      spiderElement.style.top = `${spider.y}px`
      spiderElement.style.opacity = spider.opacity.toString()
      spiderElement.style.width = `${size}px`
      spiderElement.style.height = `${frameHeight}px`
      spiderElement.style.filter = 'drop-shadow(0 0 3px rgba(0,0,0,0.3))'

      const spriteElement = document.createElement('div')
      spriteElement.style.width = '100%'
      spriteElement.style.height = '100%'
      spriteElement.style.backgroundImage = `url("${SPIDER_SPRITE_URL}")`
      spriteElement.style.backgroundRepeat = 'no-repeat'
      spriteElement.style.backgroundSize = `${size * SPIDER_FRAME_COUNT}px ${frameHeight}px`
      spriteElement.style.backgroundPosition = `${-spider.frameIndex * size}px 0`

      spiderElement.appendChild(spriteElement)
      container.appendChild(spiderElement)
      spider.element = spiderElement
      spider.sprite = spriteElement

      spiders.push(spider)
    }

    spidersRef.current = spiders

    const animate = (currentTimeValue: number) => {
      // Throttle animation to ~60fps
      if (currentTimeValue - lastUpdateRef.current < 16) {
        animationRef.current = requestAnimationFrame(animate)
        return
      }
      const delta = currentTimeValue - lastUpdateRef.current
      lastUpdateRef.current = currentTimeValue

      spidersRef.current.forEach((spider) => {
        if (!(spider.element && spider.sprite)) return

        const now = Date.now()
        const timeSinceLastChange = now - spider.lastAngleChange
        const angleChangeInterval = 2000 + Math.random() * 3000 // 2-5 seconds between changes

        // Check if we should pick a new target direction
        if (timeSinceLastChange > angleChangeInterval) {
          spider.targetAngle = spider.angle + Math.random() * 30
          spider.lastAngleChange = now
        }

        // Gradually change direction towards target angle
        let angleDiff = spider.targetAngle - spider.angle

        // Normalize angle difference to [-180, 180]
        while (angleDiff > 180) angleDiff -= 360
        while (angleDiff < -180) angleDiff += 360

        // Smooth angle transition (only change a few degrees at a time)
        const angleChange = angleDiff * 0.1 // Very gradual change
        const newAngle = spider.angle + angleChange

        // Calculate movement based on angle
        const radians = (newAngle * Math.PI) / 180
        const newX = spider.x - Math.cos(radians) * spider.speed
        const newY = spider.y - Math.sin(radians) * spider.speed

        // Keep spiders within screen bounds with some padding
        const padding = 50
        let finalX = newX
        let finalY = newY

        // Wrap around edges
        if (newX < -padding) {
          finalX = window.innerWidth + padding
        } else if (newX > window.innerWidth + padding) {
          finalX = -padding
        }

        if (newY < -padding) {
          finalY = window.innerHeight + padding
        } else if (newY > window.innerHeight + padding) {
          finalY = -padding
        }

        // Update spider data
        spider.x = finalX
        spider.y = finalY
        spider.angle = newAngle
        spider.frameElapsed += delta
        const framesAdvanced = Math.floor(spider.frameElapsed / SPIDER_FRAME_DURATION_MS)
        if (framesAdvanced > 0) {
          spider.frameElapsed -= framesAdvanced * SPIDER_FRAME_DURATION_MS
          spider.frameIndex = (spider.frameIndex + framesAdvanced) % SPIDER_FRAME_COUNT
          spider.sprite.style.backgroundPosition = `${-spider.frameIndex * spider.size}px 0`
        }

        // Update DOM directly without React re-render
        spider.element.style.left = `${finalX}px`
        spider.element.style.top = `${finalY}px`
        spider.element.style.transform = `translate(-50%, -50%) rotate(${newAngle}deg)`
      })

      animationRef.current = requestAnimationFrame(animate)
    }

    animationRef.current = requestAnimationFrame(animate)

    // Handle window resize
    const handleResize = () => {
      spidersRef.current.forEach((spider) => {
        if (!spider.element) return
        spider.x = Math.min(spider.x, window.innerWidth - 100)
        spider.y = Math.min(spider.y, window.innerHeight - 100)
        spider.element.style.left = `${spider.x}px`
        spider.element.style.top = `${spider.y}px`
      })
    }

    window.addEventListener('resize', handleResize)

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
      window.removeEventListener('resize', handleResize)

      // Clean up spider elements
      spidersRef.current.forEach((spider) => {
        if (spider.element?.parentNode) {
          spider.element.parentNode.removeChild(spider.element)
        }
      })
    }
  }, [containerMounted])

  return (
    <HolidayPortal>
      <div
        className="pointer-events-none fixed inset-0 z-100 h-screen w-screen overflow-hidden"
        ref={setContainerRef}
      />
    </HolidayPortal>
  )
}

export default SpiderWalkingEffect
